import dayjs from 'dayjs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { NULL_VALUE } from 'constants/displayConstants';
import type { CustomEventSummary } from 'hooks/customEvents/useCustomEvents';
import { useAors } from 'hooks/useAors';
import { useRosters } from 'hooks/useRosters';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import type { AreaOfResponsibility } from '../../../../types/aor';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import { useGoToGeometry } from './EventMapButton';

const formatDate = (value: string | null) =>
  value ? dayjs(value).format('MMM D, YYYY, h:mm A') : NULL_VALUE;

const getAorGeometry = (
  aor: AreaOfResponsibility | undefined
): GeoJSON.Geometry | null => {
  const geom = aor?.current_location_geom;
  if (!geom) return null;
  try {
    return JSON.parse(geom);
  } catch {
    return null;
  }
};

type EventDetailsProps = {
  event: CustomEventSummary;
};

const EventDetails: React.FC<EventDetailsProps> = ({ event }) => {
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: roster = [] } = useRosters(groupGuid ? [groupGuid] : []);
  const { data: aors = [] } = useAors();
  const { isMapVisible } = useUscgMapContext();
  const goToGeometry = useGoToGeometry();

  const creator = roster.find((member) => member.user_guid === event.createdBy);
  const creatorName = creator
    ? creator.user_name || creator.email_address
    : null;

  return (
    <>
      <Alert className="tw:border-green-600">
        <AlertDescription className="tw:text-foreground">
          Last Updated: {formatDate(event.updatedAt)}
        </AlertDescription>
      </Alert>
      <Card size="sm">
        <CardHeader>
          <CardTitle className="tw:font-semibold">Event Details</CardTitle>
        </CardHeader>
        <CardContent className="tw:grid tw:gap-4">
          <div>
            <span className="tw:font-semibold">Started:</span>{' '}
            {formatDate(event.startDatetime)}
            {creatorName && ` by ${creatorName}`}
          </div>
          {event.summary && (
            <div>
              <span className="tw:font-semibold">Summary:</span> {event.summary}
            </div>
          )}
          <div className="tw:whitespace-pre-wrap">
            <span className="tw:font-semibold">Description:</span>{' '}
            {event.description || NULL_VALUE}
          </div>
          {event.aors.length > 0 && (
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
              <span className="tw:font-semibold">AORs:</span>
              {event.aors.map((aor) => {
                const geometry = getAorGeometry(
                  aors.find((item) => item.id === aor.id)
                );
                if (!geometry || !isMapVisible) {
                  return (
                    <Badge key={aor.id} variant="count-outline">
                      {aor.name}
                    </Badge>
                  );
                }
                return (
                  <Badge
                    key={aor.id}
                    variant="count-outline"
                    className="tw:cursor-pointer tw:hover:bg-accent"
                    title="Jump to map"
                    aria-label={`Jump to ${aor.name} on map`}
                    onClick={() => void goToGeometry(geometry)}
                    render={<button type="button" />}
                  >
                    {aor.name}
                  </Badge>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default EventDetails;
